import { api } from '@/api';

export default {
  namespaced: true,
  state: {
    stylesList: [],
    typesList: [],
    archivedTypesList: [],
    presetsList: [],
    styleMaterials: [],
    unrealEnvironment: [],
    brandingsList: [],
    minimalBrandingsList: [],
    checklists: [],
    webhooks: [],
    webhookStrategies: [],
    issueCategoriesList: [],
    defaultPreset: null,
    defaultUnrealEnvironment: 'Default',
  },
  mutations: {
    setStylesList(state, styles) {
      state.stylesList = styles;
    },
    setTypesList(state, types) {
      state.typesList = types;
    },
    setArchivedTypesList(state, types) {
      state.archivedTypesList = types;
    },
    setPresetsList(state, presets) {
      state.presetsList = presets.data;
      state.defaultPreset = presets.default;
    },
    setStyleMaterialsList(state, materials) {
      state.styleMaterials = materials;
    },
    setUnrealEnvironment(state, unrealEnvironment) {
      state.unrealEnvironment = unrealEnvironment;
    },
    setBrandingsList(state, brandingsList) {
      state.brandingsList = brandingsList;
    },
    setMinimalBrandingsList(state, minimalBrandingsList) {
      state.minimalBrandingsList = minimalBrandingsList;
    },
    setChecklists(state, checklists) {
      state.checklists = checklists;
    },
    setWebhooks(state, webhooks) {
      state.webhooks = webhooks;
    },
    setWebhookStrategies(state, strategies) {
      state.webhookStrategies = strategies;
    },
    setIssueCategories(state, list) {
      state.issueCategoriesList = list;
    },
  },

  actions: {
    getStylesList(store, params = {}) {
      return api.getStylesListForFurniture(params).then((resp) => {
        const { data } = resp;
        store.commit('setStylesList', data);
        return data;
      });
    },
    createStyle(store, request) {
      return api.createStyleForFurniture(request).then((resp) => resp);
    },
    editStyle(store, request) {
      return api.editStyleForFurniture(request).then((resp) => resp);
    },
    deleteStyle(store, id) {
      return api.deleteStyleForFurniture(id).then((resp) => resp);
    },

    getIssueCategories(store) {
      return api.getIssueCategories().then((resp) => {
        store.commit('setIssueCategories', resp.data);
        return resp.data;
      });
    },
    createIssueCategory(store, request) {
      return api.createIssueCategory(request).then((resp) => resp);
    },
    editIssueCategory(store, request) {
      return api.updateIssueCategory(request).then((resp) => resp);
    },
    deleteIssueCategory(store, id) {
      return api.deleteIssueCategory(id).then((resp) => resp);
    },

    getTypesList(store) {
      return api.getRoomTypesList().then((resp) => {
        const { data } = resp;
        store.commit('setTypesList', data);
        return data;
      });
    },
    getArchivedTypesList(store) {
      return api.getRoomTypesList({ is_archived: true }).then((resp) => {
        const { data } = resp;
        store.commit('setArchivedTypesList', data);
        return data;
      });
    },
    createType(store, request) {
      return api.createRoomType(request).then((resp) => resp);
    },
    editType(store, request) {
      return api.editRoomType(request).then((resp) => resp);
    },
    archiveType(store, id) {
      return api.archiveRoomType(id).then((resp) => resp);
    },
    restoreType(store, id) {
      return api.restoreRoomType(id).then((resp) => resp);
    },

    createPreset(store, request) {
      return api.createPreset(request).then((resp) => resp);
    },
    getPresetsList(store, params = {}) {
      return api.getPresets(params).then((resp) => {
        const { data } = resp;
        store.commit('setPresetsList', data);
        return data;
      });
    },
    editPreset(store, request) {
      return api.editPreset(request).then((resp) => resp);
    },
    deletePreset(store, id) {
      return api.deletePreset(id).then((resp) => resp);
    },

    getStyleMaterialsList(store) {
      return api.getStyleMaterials().then((resp) => {
        const { data } = resp;
        store.commit('setStyleMaterialsList', data);
        return data;
      });
    },
    createStyleMaterial({ state, commit }, request) {
      return api.createStyleMaterial(request).then(({ data }) => {
        commit('setStyleMaterialsList', [
          ...state.styleMaterials,
          data,
        ]);
        return data;
      });
    },
    deleteStyleMaterial({ state, commit }, id) {
      return api.deleteStyleMaterial(id).then(() => {
        commit('setStyleMaterialsList', state.styleMaterials.filter((item) => item.id !== id));
      });
    },

    getUnrealEnvironment(store) {
      return api.getUnrealEnvironment().then((resp) => {
        const { data } = resp;
        store.commit('setUnrealEnvironment', data);
        return data;
      });
    },

    createUnrealEnvironment(store, request) {
      return api.createUnrealEnvironment(request).then((resp) => resp);
    },

    editUnrealEnvironment(store, request) {
      return api.editUnrealEnvironment(request).then((resp) => resp);
    },

    async invalidateUnrealEnvironment() {
      return api.invalidateUnrealEnvironment().then((resp) => resp);
    },

    deleteUnrealEnvironment(store, id) {
      return api.deleteUnrealEnvironment(id).then((resp) => resp);
    },

    restoreUnrealEnvironment(store, id) {
      return api.restoreUnrealEnvironment(id).then((resp) => resp);
    },

    getBrandingsList(store) {
      return api.getBrandingsList().then((resp) => {
        const { data } = resp;
        store.commit('setBrandingsList', data);
        return data;
      });
    },

    getMinimalBrandingsList(store) {
      return api.getMinimalBrandingsList().then((resp) => {
        const { data } = resp;
        store.commit('setMinimalBrandingsList', data);
        return data;
      });
    },

    getChecklists(store) {
      return api.getChecklists().then((resp) => {
        const { data } = resp;
        store.commit('setChecklists', data);
        return data;
      });
    },

    getWebhooks(store) {
      return api.getWebhooks().then((resp) => {
        const { data } = resp;
        store.commit('setWebhooks', data);
        return data;
      });
    },

    getWebhookStrategies(store) {
      return api.getWebhookStrategies().then((resp) => {
        const { data } = resp;
        store.commit('setWebhookStrategies', data);
        return data;
      });
    },

    getWebhook(store, id) {
      return api.getWebhook(id).then((resp) => resp.data);
    },

    createWebhook(store, request) {
      return api.createWebhook(request).then((resp) => resp.data);
    },

    updateWebhook(store, { id, payload }) {
      return api.updateWebhook(id, payload).then((resp) => resp.data);
    },

    deleteWebhook(store, id) {
      return api.deleteWebhook(id).then((resp) => resp.data);
    },

    createBranding(store, request) {
      return api.createBranding(request).then((resp) => resp);
    },

    createChecklist(store, request) {
      return api.createChecklist(request).then((resp) => resp);
    },

    editBranding(store, request) {
      return api.editBranding(request).then((resp) => resp);
    },

    editChecklist(store, request) {
      return api.editChecklist(request).then((resp) => resp);
    },

    deleteChecklist(store, id) {
      return api.deleteChecklist(id).then((resp) => resp);
    },

    createLanding(store, request) {
      return api.editLanding(request).then((resp) => resp);
    },

    editLanding(store, request) {
      return api.editLanding(request).then((resp) => resp);
    },

    deleteBranding(store, id) {
      return api.deleteBranding(id).then((resp) => resp);
    },

    createLandingTariff({ commit }, { landingId, formData }) {
      return api.createLandingTariff(landingId, formData).then((resp) => resp);
    },

    updateLandingTariff({ commit }, { landingId, tariffId, formData }) {
      return api.updateLandingTariff(landingId, tariffId, formData).then((resp) => resp);
    },
    deleteLandingTariff({ commit }, { tariffId }) {
      return api.deleteLandingTariff(tariffId).then((resp) => resp);
    },
  },
};
